import type { PersoonRij } from '@vip/shared';
import { isAchterstallig, peilmoment, sorteerOpStilte, wekenSinds } from '@vip/shared';

/**
 * Twee kolommen uit één platte lijst personen.
 *
 * Pure functies met tests, zoals `gesprekken/zoeken.ts` en
 * `onderwerpen/filter.ts`. De server levert één rij per mens; het groeperen
 * gebeurt hier.
 *
 * De opdracht is hier géén structuur meer. Coach en gecoachte zitten vaak niet
 * op dezelfde opdracht, en een buddy wordt zelf ook begeleid; op opdracht
 * groeperen zette dezelfde mens daardoor op drie plekken en liet iedereen
 * zonder opdracht helemaal buiten beeld. Nu staat links iedereen als iemand die
 * een buddy nodig heeft en rechts iedereen die er een kan zijn. De opdracht is
 * één regel tekst onder de naam.
 */

/** Alleen wie meedoet: het archief is een eigen tabblad, en wie uitstaat hoort
 *  niet in een overzicht van lopende begeleiding. */
export const doetMee = (p: PersoonRij) => !p.gearchiveerdOp && p.actief;

const opNaam = (a: PersoonRij, b: PersoonRij) => a.naam.localeCompare(b.naam, 'nl');

export interface Kaart {
  buddy: PersoonRij;
  gecoachten: PersoonRij[];
  /** Het oudste peilmoment onder zijn gecoachten; hierop staan de kaarten
   *  gesorteerd. Leeg bij wie niemand begeleidt, en die kaarten staan onderaan:
   *  het signaal hoort bij de begeleide persoon en niet bij de buddy. */
  peiling: string | null;
}

/**
 * Eén kaart per buddy. Alleen zij leveren een gesprek aan en kunnen dus
 * begeleiden; daarmee zijn dit precies de plekken waar iemand naartoe gesleept
 * kan worden.
 *
 * Dit filterde op "kan inloggen", en dat viel samen met "is buddy" zolang de
 * rechten één trap waren waarop de Admin bovenaan stond. Sinds buddy en
 * Admin losse vinkjes zijn niet meer: elke Admin kreeg hier een kaart,
 * en dus stond dit scherm vol met mensen die nooit een gesprek aanleveren.
 *
 * `eigenId` zet de kaart van wie kijkt bovenaan. Zonder dat zakt je eigen kaart
 * juist weg zodra je begeleiding bij is, want de rest staat op wie het langst
 * geen gesprek had. Voor een buddy is dit scherm sinds kort ook zijn eigen
 * roster, en dan is het vreemd om jezelf onderaan terug te vinden.
 */
export function kaarten(personen: readonly PersoonRij[], eigenId?: string): Kaart[] {
  const actief = personen.filter(doetMee);
  return actief
    .filter((p) => p.rechten.buddy)
    .map((buddy) => {
      const gecoachten = sorteerOpStilte(actief.filter((p) => p.buddy?.id === buddy.id));
      const langst = gecoachten[0];
      return { buddy, gecoachten, peiling: langst ? peilmoment(langst) : null };
    })
    .sort((a, b) => {
      // De eigen kaart staat buiten de rangschikking: die hoort bovenaan,
      // hoe goed of slecht de begeleiding erop ook loopt.
      if (a.buddy.id === b.buddy.id) return 0;
      if (a.buddy.id === eigenId) return -1;
      if (b.buddy.id === eigenId) return 1;
      if (a.peiling !== null && b.peiling !== null) return a.peiling.localeCompare(b.peiling);
      if (a.peiling !== null) return -1;
      if (b.peiling !== null) return 1;
      return opNaam(a.buddy, b.buddy);
    });
}

/**
 * Wie nog geen buddy heeft: de linkerkolom, en de enige lijst die het scherm van
 * de platte personenlijst maakt.
 *
 * Wie er wel een heeft stond hier ook als lijst. Die is eruit: dezelfde mensen
 * staan rechts al onder hun buddy, en dat is de plek die zegt onder wie.
 *
 * De lijst kan leeg raken, ook al staat een senior erin: ook hij mag een buddy
 * hebben.
 */
export function zonderBuddy(personen: readonly PersoonRij[]): PersoonRij[] {
  return personen
    .filter(doetMee)
    .filter((p) => !p.buddy)
    .sort(opNaam);
}

/** Van gecoachte naar zijn buddy, voor de controle op een rondje in de
 *  ketting. Dezelfde vorm die de server gebruikt. */
export function keten(personen: readonly PersoonRij[]): Map<string, string> {
  const rijen = new Map<string, string>();
  for (const p of personen) if (p.buddy) rijen.set(p.id, p.buddy.id);
  return rijen;
}

/** Of deze naam de zoekterm bevat. Niets zoeken laat alles staan. */
export function past(p: PersoonRij, zoek: string): boolean {
  const term = zoek.trim().toLowerCase();
  return term === '' || p.naam.toLowerCase().includes(term);
}

/** Een kaart blijft staan als de buddy zelf of een van zijn mensen wordt
 *  gezocht. Zoeken op een gecoachte zou anders juist de plek verbergen waar hij
 *  hangt. */
export function filterKaarten(rijen: readonly Kaart[], zoek: string): Kaart[] {
  if (zoek.trim() === '') return [...rijen];
  return rijen.filter((k) => past(k.buddy, zoek) || k.gecoachten.some((p) => past(p, zoek)));
}

/** Of deze rij te lang stilstaat. Dun laagje over de gedeelde regel, zodat het
 *  scherm niet op twee plekken hetzelfde object hoeft samen te stellen. */
export function staatStil(p: PersoonRij, drempelWeken: number, nu: string): boolean {
  return isAchterstallig(
    { laatsteGesprek: p.laatsteGesprek, toegevoegdOp: p.toegevoegdOp },
    drempelWeken,
    nu,
  );
}

/**
 * Hoe lang het stil is, in de kortste vorm die nog iets zegt.
 *
 * Weken en geen datum: de vraag is niet wanneer het laatste gesprek was maar hoe
 * lang het geleden is, en dat rekent niemand in zijn hoofd uit een datum om.
 */
export function stilteTekst(p: PersoonRij, nu: string): string {
  if (!p.laatsteGesprek) return 'nog geen gesprek';
  const weken = wekenSinds(p.laatsteGesprek, nu);
  if (weken === 0) return 'deze week';
  if (weken === 1) return '1 week';
  return `${weken} weken`;
}
